import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  canonicalPath: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_URL = 'https://learnwebacademy.net';
const DEFAULT_IMAGE = `${SITE_URL}/LOGO-web-academy.svg`;
const SCHEMA_SCRIPT_ID = 'structured-data';

const setMetaTag = (selector: string, attribute: 'content' | 'href', value: string) => {
  const element = document.head.querySelector(selector);

  if (element) {
    element.setAttribute(attribute, value);
  }
};

const ensureCanonicalLink = () => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  return canonical;
};

const updateSchemaMarkup = (schema: Record<string, unknown> | Record<string, unknown>[]) => {
  let script = document.head.querySelector<HTMLScriptElement>(`#${SCHEMA_SCRIPT_ID}`);

  if (!script) {
    script = document.createElement('script');
    script.id = SCHEMA_SCRIPT_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

const getDefaultSchema = (title: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'LearnWebAcademy',
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'LearnWebAcademy',
      url: SITE_URL,
      publisher: {
        '@id': `${SITE_URL}/#organization`,
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: {
        '@id': `${SITE_URL}/#website`,
      },
      about: {
        '@id': `${SITE_URL}/#organization`,
      },
    },
  ],
});

const Seo = ({ title, description, canonicalPath, schema }: SeoProps) => {
  useEffect(() => {
    const url = `${SITE_URL}${canonicalPath}`;
    const schemaMarkup = schema || getDefaultSchema(title, description, url);

    document.title = title;

    setMetaTag('meta[name="description"]', 'content', description);
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', url);
    setMetaTag('meta[property="og:image"]', 'content', DEFAULT_IMAGE);
    ensureCanonicalLink().href = url;
    updateSchemaMarkup(schemaMarkup);
  }, [canonicalPath, description, schema, title]);

  return null;
};

export default Seo;
